import React, { useState } from "react";
import './CoordinateInputTool.css';
import { useLanguage } from "./i18n";
import BulkImportTool from "./BulkImportTool";

import { parseCoordinateRows } from "./utils/coordinateInputParser";

function getPlaceholder(coordFormat, type, t) {
  const examples = {
    dd: ['59.76645278, 16.40706111, 100', '59.76666667, 16.41666667, 100', '59.77, 16.41, 100'],
    dm: ['5945.98717N, 1624.42367E, 100', '5946.00000N, 1625.00000E, 100', '5946.20000N, 1624.60000E, 100'],
    dms: ["59°45'59.23\"N, 16°24'25.42\"E, 100", "59°46'00.00\"N, 16°25'00.00\"E, 100", "59°46'12.00\"N, 16°24'36.00\"E, 100"],
  };
  const count = type === 'Polygon' ? 3 : type === 'LineString' ? 2 : 1;
  return [t('coordinateInput_rowHint'), ...(examples[coordFormat] || []).slice(0, count)].join('\n');
}

export default function CoordinateInputTool({ onAdd }) {
  const { t } = useLanguage();
  const [type, setType] = useState("Point");
  const [coords, setCoords] = useState("");
  const [name, setName] = useState("");
  const [radius, setRadius] = useState("");
  const [radiusUnit, setRadiusUnit] = useState("meter");
  const [zoomTo, setZoomTo] = useState(true);
  const [coordFormat, setCoordFormat] = useState("dms");
  const [error, setError] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  function handleAdd() {
    const parsed = parseCoordinateRows(coords, coordFormat);
    if (!parsed.length) {
      setError(t("coordinateInput_error"));
      return;
    }
    setError("");

    let geo = null;
    if (type === "Point") {
      geo = { type: "Point", coordinates: parsed[0] };
    } else if (type === "LineString") {
      geo = { type: "LineString", coordinates: parsed };
    } else if (type === "Polygon") {
      geo = { type: "Polygon", coordinates: [parsed] };
    }
    if (type === "Circle") {
      geo = {
        type: "Circle",
        center: parsed[0],
        radius: parseFloat(radius) * (radiusUnit === "NM" ? 1852 : 1),
        z: parsed[0][2]
      };
    }
    onAdd && onAdd({ geometry: geo, properties: { name } }, { zoomTo });
  }

  function handleBulkImport(objects) {
    objects.forEach(obj => {
      onAdd && onAdd(obj, { zoomTo });
    });
    setImportOpen(false);
  }

  return (
    <div className="coordinate-input-tool">
      {/* Geometry type selector */}
      <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
        <label><input type="radio" checked={type==="Point"} onChange={()=>setType("Point")}/> {t("coordinateInput_point")}</label>
        <label><input type="radio" checked={type==="LineString"} onChange={()=>setType("LineString")}/> {t("coordinateInput_line")}</label>
        <label><input type="radio" checked={type==="Polygon"} onChange={()=>setType("Polygon")}/> {t("coordinateInput_polygon")}</label>
        <label><input type="radio" checked={type==="Circle"} onChange={()=>setType("Circle")}/> {t("coordinateInput_circle")}</label>
      </div>

      {/* Manual coordinate entry */}
      <div style={{ marginBottom: 8 }}>
        <label>{t("coordinateInput_coordFormat")} </label>
        <select value={coordFormat} onChange={e => setCoordFormat(e.target.value)}>
          <option value="dd">DD.dddddd</option>
          <option value="dm">DM.mmmmm</option>
          <option value="dms">DMS / DD°MM'SS.s"</option>
        </select>
      </div>

      {/* Coordinate format help */}
      <div style={{ marginBottom: 8, padding: 8, backgroundColor: "#f5f5f5", borderRadius: 4, fontSize: "0.9em" }}>
        <div style={{ fontWeight: "bold", marginBottom: 4 }}>{t("coordinateInput_formatHelp")}</div>
        <div>{t("coordinateInput_formatHelp_dd")}</div>
        <div>{t("coordinateInput_formatHelp_dm")}</div>
        <div>{t("coordinateInput_formatHelp_dms")}</div>
      </div>
      <textarea
        value={coords}
        onChange={e=>setCoords(e.target.value)}
        rows={5}
        style={{ width: "100%" }}
        placeholder={getPlaceholder(coordFormat, type, t)}
      />
      {type === "Circle" && (
        <div style={{ margin: "8px 0" }}>
          <label>
            {t("coordinateInput_radius")} <input value={radius} onChange={e=>setRadius(e.target.value)} style={{ width: 60 }}/>
          </label>
          <label>
            <input type="radio" checked={radiusUnit==="NM"} onChange={()=>setRadiusUnit("NM")}/> {t("coordinateInput_unit_nm")}
          </label>
          <label>
            <input type="radio" checked={radiusUnit==="meter"} onChange={()=>setRadiusUnit("meter")}/> {t("coordinateInput_unit_meter")}
          </label>
        </div>
      )}
      <div style={{ margin: "8px 0" }}>
        {t("coordinateInput_name")} <input value={name} onChange={e=>setName(e.target.value)} style={{ width: 120 }}/>
        <button onClick={()=>{
          setCoords("");
          setName("");
          setRadius("");
          setError("");
        }}>{t("coordinateInput_clear")}</button>
      </div>
      <div style={{ margin: "8px 0" }}>
        <label>
          <input type="checkbox" checked={zoomTo} onChange={e=>setZoomTo(e.target.checked)}/>
          {t("coordinateInput_zoom")}
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
        <button onClick={handleAdd}>{t("coordinateInput_addToMap")}</button>
        <button
          className="coord-upload-btn"
          onClick={() => setImportOpen(true)}
          title={t("bulkImport_uploadFile")}
        >
          {t("coordinateInput_uploadFile")}
        </button>
      </div>
      {error && <div style={{ color: "red", marginTop: 6 }}>{error}</div>}

      {/* ── Import modal ── */}
      {importOpen && (
        <div
          className="coord-import-overlay"
          onClick={() => setImportOpen(false)}
        >
          <div
            className="coord-import-dialog"
            onClick={e => e.stopPropagation()}
          >
            <div className="coord-import-dialog-header">
              <span>{t("coordinateInput_importTitle")} – {t(`coordinateInput_${type.toLowerCase()}`) || type}</span>
              <button
                className="coord-import-dialog-close"
                onClick={() => setImportOpen(false)}
                title={t("close")}
              >×</button>
            </div>
            <div className="coord-import-dialog-body">
              <BulkImportTool
                geometryType={type}
                onImport={handleBulkImport}
                onCancel={() => setImportOpen(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}